import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ArrowTrendingDownIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { effectiveCapitalApi } from '../../helpers/effectiveCapitalApi';
import { accountOptionLabel, buildAccountOptions } from '../../helpers/transactionAccountOptions';
import { parseAmountQuery, transactionMatchesSearch } from '../../helpers/transactionAmountSearch';
import { LoaderSpinner } from '../LoaderSpinner';
import { SelectDropdown } from '../SelectDropdown';
import { DatePickerInput } from '../DatePickerInput';

const ENTITY_OPTIONS = [
  { label: 'All Entities', value: '' },
  { label: 'Kings Loot', value: 'kings_loot' },
  { label: 'Kings Holding', value: 'kings_holding' },
];

const DATE_RANGE_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last7' },
  { label: 'Last 30 days', value: 'last30' },
  { label: 'Month to date', value: 'mtd' },
  { label: 'Year to date', value: 'ytd' },
  { label: 'Custom', value: 'custom' },
];

function toIsoDate(d) {
  return d.toISOString().slice(0, 10);
}

function getPresetRange(preset) {
  const now = new Date();
  const today = toIsoDate(now);
  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case 'yesterday': {
      const y = toIsoDate(new Date(now.getTime() - 24 * 60 * 60 * 1000));
      return { start: y, end: y };
    }
    case 'last7':
      return { start: toIsoDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)), end: today };
    case 'last30':
      return { start: toIsoDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)), end: today };
    case 'mtd':
      return { start: `${today.slice(0, 7)}-01`, end: today };
    case 'ytd':
      return { start: `${today.slice(0, 4)}-01-01`, end: today };
    default:
      return null;
  }
}

const CAT_COLORS = ['#1a1a1a', '#333', '#4d4d4d', '#666', '#808080', '#999', '#b3b3b3', '#ccc'];

function formatDate(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatAmount(amount) {
  const n = parseFloat(String(amount));
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Math.abs(n));
}

function formatCurrencyShort(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function KpiCard({ label, value, sub, Icon }) {
  return (
    <div className="kpi-card flex flex-col gap-1">
      <div className="kpi-card-label flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3 shrink-0" />}
        {label}
      </div>
      <div className="kpi-card-value">{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-300 px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium">{label}</div>
      <div className="tabular-nums">{formatCurrencyShort(payload[0].value)}</div>
    </div>
  );
}

const SYNC_ROLES = ['ADMIN', 'FINANCE'];

export const EffectiveCapitalSummaryTransactions = ({ onNavigateTab, onLoadingChange, viewSwitcher }) => {
  const { currentUser } = useSelector((state) => state.auth);
  const canSync = SYNC_ROLES.includes(currentUser?.role);
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [datePreset, setDatePreset] = useState('last30');
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const range = getPresetRange(preset);
    if (range) {
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };
  const [entityFilter, setEntityFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showTable, setShowTable] = useState(true);

  const [transactions, setTransactions] = useState([]);
  const [catData, setCatData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isIncrementalSyncing, setIsIncrementalSyncing] = useState(false);
  const [isRecategorizing, setIsRecategorizing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const knownAccountLabels = useRef(new Map());

  const queryParams = useMemo(
    () => ({
      startDate,
      endDate,
      entity: entityFilter || undefined,
    }),
    [startDate, endDate, entityFilter]
  );

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [txList, spending, monthly, status] = await Promise.all([
        effectiveCapitalApi.listTransactions(queryParams),
        effectiveCapitalApi.getSpendingByCategory({
          startDate,
          endDate,
          entity: entityFilter || undefined,
        }),
        effectiveCapitalApi.getMonthlyTotals({
          months: 12,
          entity: entityFilter || undefined,
        }),
        effectiveCapitalApi.getTransactionsSyncStatus(),
      ]);
      setTransactions(txList);
      setCatData(spending);
      setMonthlyData(monthly);
      setSyncStatus(status);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to load transactions', { position: 'bottom-right', toastId: 'tx-load-error' });
    } finally {
      setIsLoading(false);
    }
  }, [queryParams, startDate, endDate, entityFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    for (const tx of transactions) {
      if (tx.plaidAccountId) {
        knownAccountLabels.current.set(tx.plaidAccountId, accountOptionLabel(tx));
      }
    }
  }, [transactions]);

  const monthlyChartData = useMemo(() => {
    const byMonth = {};
    for (const row of monthlyData) {
      byMonth[row.month] = (byMonth[row.month] ?? 0) + row.total;
    }
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, total]) => {
        const [y, m] = month.split('-');
        const label = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit',
        });
        return { month: label, total };
      });
  }, [monthlyData]);

  const amountQuery = useMemo(() => parseAmountQuery(search), [search]);
  const accountOptions = useMemo(
    () => buildAccountOptions(transactions, { selected: accountFilter, knownLabels: knownAccountLabels.current }),
    [transactions, accountFilter]
  );

  const filteredTx = useMemo(() => {
    return transactions.filter((tx) => {
      const matchesSearch = transactionMatchesSearch(tx, search);
      const matchesAccount = !accountFilter || tx.plaidAccountId === accountFilter;
      const matchesCategory = !categoryFilter || (tx.category ?? 'Uncategorized') === categoryFilter;
      return matchesSearch && matchesAccount && matchesCategory;
    });
  }, [transactions, search, accountFilter, categoryFilter]);

  const filteredKpis = useMemo(() => {
    const postedSpend = filteredTx.filter((tx) => Number(tx.amount) > 0 && Number(tx.pending) === 0);
    const byCategory = {};
    for (const tx of postedSpend) {
      const category = tx.category ?? 'Uncategorized';
      byCategory[category] = (byCategory[category] ?? 0) + Number(tx.amount);
    }
    const topCategory = Object.entries(byCategory).sort(([, a], [, b]) => b - a)[0]?.[0] ?? '—';
    return {
      totalSpend: postedSpend.reduce((total, tx) => total + Number(tx.amount), 0),
      transactionCount: postedSpend.length,
      topCategory,
    };
  }, [filteredTx]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const result = await effectiveCapitalApi.syncTransactions(startDate, endDate);
      const errs = result?.errors?.length ?? 0;
      const skipped = result?.skipped?.length ?? 0;
      if (errs > 0 && result.synced === 0) {
        toast.warning(result.errors.join('; '), { position: 'bottom-right' });
      } else if (result.synced === 0 && skipped > 0) {
        toast.warning(
          `Synced 0 transactions — ${skipped} connection${skipped === 1 ? '' : 's'} skipped: ${result.skipped
            .map((s) => `${s.institutionName ?? s.itemId} (${s.reason})`)
            .join('; ')}`,
          { position: 'bottom-right' }
        );
      } else if (result.synced === 0) {
        toast.info(
          'Plaid returned 0 transactions for this range. Accounts connected before extended history was enabled only carry ~90 days of history — reconnect them via Connect Accounts to pull up to 24 months.',
          { position: 'bottom-right', autoClose: 12000 }
        );
      } else {
        toast.success(`Synced ${result.synced} transactions`, { position: 'bottom-right' });
      }
      loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Sync failed', { position: 'bottom-right' });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleIncrementalSync = async () => {
    setIsIncrementalSyncing(true);
    try {
      const result = await effectiveCapitalApi.syncTransactionsIncremental();
      const errs = result?.errors?.length ?? 0;
      if (errs > 0 && result.synced === 0) {
        toast.warning(result.errors.join('; '), { position: 'bottom-right' });
      } else {
        toast.success(`Incremental sync: ${result.synced} transactions`, { position: 'bottom-right' });
      }
      loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Incremental sync failed', { position: 'bottom-right' });
    } finally {
      setIsIncrementalSyncing(false);
    }
  };

  const handleRecategorize = async () => {
    setIsRecategorizing(true);
    try {
      const result = await effectiveCapitalApi.bulkRecategorize();
      toast.success(`Re-categorized ${result.updated} of ${result.total} transactions`, { position: 'bottom-right' });
      loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Re-categorize failed', { position: 'bottom-right' });
    } finally {
      setIsRecategorizing(false);
    }
  };

  const exportCSV = () => {
    if (!filteredTx.length) {
      toast.error('No transactions to export', { position: 'bottom-right' });
      return;
    }

    const headers = ['Date', 'Entity', 'Account', 'Description', 'Merchant', 'Category', 'Amount', 'Status'];
    const rows = filteredTx.map((tx) => [
      tx.date,
      tx.entity,
      tx.accountCustomName ?? tx.accountName ?? tx.plaidAccountId,
      `"${(tx.name ?? '').replace(/"/g, '""')}"`,
      tx.merchantName ?? '',
      tx.category ?? '',
      parseFloat(String(tx.amount)).toFixed(2),
      tx.pending ? 'Pending' : 'Posted',
    ]);

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredTx.length} transactions`, { position: 'bottom-right' });
  };

  return (
    <div className="flex flex-col">
      <div className="flex shrink-0 flex-col gap-3 py-3 border-b border-gray-200 mb-4">
        <div>
          {viewSwitcher || <h2 className="text-sm font-bold uppercase tracking-widest">Transactions</h2>}
          <p className="text-xs text-gray-500">Spending analytics and drill-down</p>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end">
          <div className="min-w-0 lg:w-[160px]">
            <SelectDropdown
              options={DATE_RANGE_OPTIONS}
              value={datePreset}
              mainWrapperClassname="!w-full lg:!w-[160px]"
              itemValueClassname="px-2 py-1 text-sm text-left"
              selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
              selectClassname="!bg-white !px-3 !py-2 !border-input !w-full"
              dropdownListClassname="!max-h-none"
              onChange={handlePresetChange}
            />
          </div>
          {datePreset === 'custom' && (
            <>
              <div className="flex flex-col gap-1 min-w-0">
                <label className="text-[11px] font-medium uppercase tracking-wider text-gray-500">From</label>
                <div className="w-full lg:w-40">
                  <DatePickerInput value={startDate} onChange={setStartDate} placeholder="Start date" />
                </div>
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <label className="text-[11px] font-medium uppercase tracking-wider text-gray-500">To</label>
                <div className="w-full lg:w-40">
                  <DatePickerInput value={endDate} onChange={setEndDate} placeholder="End date" min={startDate || undefined} />
                </div>
              </div>
            </>
          )}
          <div className="min-w-0 sm:col-span-2 lg:col-span-1 lg:w-[140px]">
            <SelectDropdown
              options={ENTITY_OPTIONS}
              value={entityFilter}
              placeholder="All Entities"
              mainWrapperClassname="!w-full lg:!w-[140px]"
              itemValueClassname="px-2 py-1 text-sm text-left"
              selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
              selectClassname="!bg-white !px-3 !py-2 !border-input !w-full"
              onChange={setEntityFilter}
            />
          </div>
          <div className="min-w-0 sm:col-span-2 lg:col-span-1 lg:w-auto">
            <SelectDropdown
              options={accountOptions}
              value={accountFilter}
              placeholder="All Accounts"
              mainWrapperClassname="!w-full lg:!w-auto"
              itemValueClassname="px-2 py-1 text-sm text-left"
              selectedValueClassname="min-w-0 whitespace-nowrap overflow-hidden text-ellipsis"
              selectClassname="!bg-white !px-3 !py-2 !border-input !w-full lg:!w-auto lg:!min-w-[200px] lg:!max-w-[340px] lg:!gap-2"
              onChange={setAccountFilter}
              menuSearchable
              searchPlaceholder="Name or last 4…"
              expandMenuWidth
              disabled={accountOptions.length <= 1}
            />
          </div>
          <div className="relative min-w-0 sm:col-span-2 lg:col-span-1 lg:w-44">
            <MagnifyingGlassIcon className="shrink-0 absolute left-2.5 top-1/2 w-3.5 h-3.5 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search or amount ($45, >100, 45-60)"
              title="Search text, amounts, or card digits: $45, >100, *1234, card:1234, or 45-60"
              aria-label="Search transactions by text, amount, or card digits, such as $45, greater than 100, *1234, card:1234, or 45 to 60"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg bg-white py-2 pl-8 pr-7 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                <XMarkIcon className="w-3.5 h-3.5 shrink-0" />
              </button>
            )}
          </div>
        </div>
        {amountQuery && <div className="text-xs text-gray-500">Amount match: {amountQuery.label}</div>}

        <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center">
          {canSync && (
            <>
              <button type="button" onClick={handleIncrementalSync} disabled={isIncrementalSyncing} className="flex items-center justify-center gap-1.5 border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50" title="Incremental cursor-based sync">
                <ArrowPathIcon className={`w-4 h-4 shrink-0 ${isIncrementalSyncing ? 'animate-spin' : ''}`} />
                Sync Latest
              </button>
              <button type="button" onClick={handleSync} disabled={isSyncing} className="flex items-center justify-center gap-1.5 border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50" title="Full sync for date range">
                <ArrowPathIcon className={`w-4 h-4 shrink-0 ${isSyncing ? 'animate-spin' : ''}`} />
                Sync Range
              </button>
              <button type="button" onClick={handleRecategorize} disabled={isRecategorizing} className="col-span-2 flex items-center justify-center gap-1.5 border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 lg:col-span-1" title="Apply QBO category rules">
                {isRecategorizing ? 'Applying...' : 'Apply QBO Rules'}
              </button>
            </>
          )}
          <button type="button" onClick={exportCSV} disabled={!filteredTx.length} className="col-span-2 flex items-center justify-center gap-1.5 border bg-primary px-4 rounded-lg py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 lg:col-span-1">
            <ArrowDownTrayIcon className="w-5 h-5 shrink-0" />
            Export CSV ({filteredTx.length})
          </button>
        </div>
      </div>

      {syncStatus?.needsPlaidReconnect && (
        <div className="mb-4 border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
          <strong>No transaction data yet.</strong>
          {(syncStatus?.plaidItems?.envMismatch ?? 0) > 0 ? (
            <>
              {' '}Connected accounts use production Plaid tokens but this server is configured for{' '}
              <strong>{syncStatus?.plaidEnv ?? 'sandbox'}</strong>. Set <code>PLAID_ENV=production</code> with production credentials, or reconnect accounts via Plaid Link in this environment.
            </>
          ) : (
            <>
              {' '}Account balances were imported from migration, but those Plaid connections use placeholder tokens and cannot sync transactions.
            </>
          )}
          {canSync ? (
            <>
              {' '}Reconnect institutions via{' '}
              <button type="button" onClick={() => onNavigateTab?.('connect-accounts')} className="underline font-medium">
                Connect Accounts
              </button>
              , then run <strong>Sync Range</strong> or <strong>Sync Latest</strong>.
            </>
          ) : (
            ' Ask an admin to reconnect Plaid and run a transaction sync.'
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-gray-500 mt-10">
          <LoaderSpinner height="80" width="80" colorSpinner="black" border="4" />
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiCard
              label="Total Spend"
              value={formatCurrencyShort(filteredKpis.totalSpend)}
              sub={`${startDate} → ${endDate}${search.trim() || accountFilter || categoryFilter ? ' · filtered' : ''}`}
              Icon={ArrowTrendingDownIcon}
            />
            <KpiCard label="Transactions" value={String(filteredKpis.transactionCount)} sub="Posted only" Icon={HashtagIcon} />
            <KpiCard label="Top Category" value={filteredKpis.topCategory} sub="By spend volume" Icon={TagIcon} />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="border border-gray-200">
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-widest border-b border-gray-200">
                Spending by Category
                {categoryFilter && (
                  <button type="button" onClick={() => setCategoryFilter('')} className="ml-2 font-normal normal-case text-gray-500 hover:text-gray-900">
                    (clear: {categoryFilter})
                  </button>
                )}
              </div>
              <div className="px-2 py-3">
                {catData.length === 0 ? (
                  <p className="flex h-44 items-center justify-center text-xs text-gray-500">No data for this period</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={catData.slice(0, 8)}
                      layout="vertical"
                      margin={{ top: 0, right: 8, left: 0, bottom: 0 }}
                      onClick={(e) => {
                        if (e?.activeLabel) {
                          setCategoryFilter(categoryFilter === e.activeLabel ? '' : e.activeLabel);
                        }
                      }}
                    >
                      <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="category" width={130} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="total" radius={0} cursor="pointer">
                        {catData.slice(0, 8).map((entry) => (
                          <Cell
                            key={entry.category}
                            fill={categoryFilter && categoryFilter !== entry.category ? '#ddd' : CAT_COLORS[catData.indexOf(entry) % CAT_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div className="border border-gray-200">
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-widest border-b border-gray-200">
                Monthly Spend — Last 12 Months
              </div>
              <div className="px-2 py-3">
                {monthlyChartData.length === 0 ? (
                  <p className="flex h-44 items-center justify-center text-xs text-gray-500">No data yet</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={monthlyChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#eee" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
                      <Tooltip content={<ChartTooltip />} />
                      <Line type="monotone" dataKey="total" stroke="#1a1a1a" strokeWidth={1.5} dot={{ r: 3, fill: '#1a1a1a' }} activeDot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          <div className="border border-gray-200">
            <button
              type="button"
              onClick={() => setShowTable((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:bg-gray-50 border-b border-gray-200"
            >
              <span>
                Transaction Detail
                {categoryFilter && <span className="ml-2 font-normal normal-case text-gray-500">— {categoryFilter}</span>}
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                {filteredTx.length} rows
                {showTable ? <ChevronUpIcon className="w-3 h-3 shrink-0" /> : <ChevronDownIcon className="w-3 h-3 shrink-0" />}
              </span>
            </button>

            {showTable && (
              <div className="overflow-auto">
                {filteredTx.length === 0 ? (
                  <div className="flex h-32 flex-col items-center justify-center gap-2 text-sm text-gray-500">
                    <p>No transactions found for the selected period.</p>
                    {canSync && (
                      <button type="button" onClick={handleSync} className="text-xs underline">Sync transactions now</button>
                    )}
                  </div>
                ) : (
                  <>
                  <table className="eadin-table hidden md:table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Entity</th>
                        <th>Description</th>
                        <th>Merchant</th>
                        <th>Account</th>
                        <th>Category</th>
                        <th className="!text-right">Amount</th>
                        <th className="!text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTx.map((tx) => {
                        const amount = parseFloat(String(tx.amount));
                        const acctLabel = tx.accountCustomName ?? tx.accountName;
                        return (
                          <tr key={tx.id}>
                            <td className="px-3 py-2 text-sm tabular-nums text-gray-500">{formatDate(tx.date)}</td>
                            <td className="px-3 py-2 text-xs">
                              <span className="border border-gray-300 px-1.5 py-0.5 text-xs">{tx.entity === 'kings_loot' ? 'KL' : 'KH'}</span>
                            </td>
                            <td className="px-3 py-2 text-sm max-w-xs truncate">{tx.name}</td>
                            <td className="px-3 py-2 text-sm text-gray-500">{tx.merchantName ?? '—'}</td>
                            <td className="px-3 py-2 text-xs text-gray-500 max-w-[120px] truncate lg:max-w-none lg:whitespace-nowrap lg:overflow-visible">
                              {acctLabel}
                              {tx.accountMask && <span className="ml-1.5 text-gray-400 tabular-nums">··{tx.accountMask}</span>}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {tx.category ? (
                                <button
                                  type="button"
                                  onClick={() => setCategoryFilter(categoryFilter === tx.category ? '' : (tx.category ?? ''))}
                                  className={`px-1.5 py-0.5 text-xs transition-colors ${
                                    categoryFilter === tx.category
                                      ? 'bg-gray-900 text-white'
                                      : 'text-gray-500 hover:text-gray-900'
                                  }`}
                                >
                                  {tx.category}
                                </button>
                              ) : (
                                <span className="text-gray-500">—</span>
                              )}
                            </td>
                            <td className={`px-3 py-2 text-right tabular-nums text-sm font-medium ${amount > 0 ? 'value-negative' : 'value-positive'}`}>
                              {amount > 0 ? '-' : '+'}
                              {formatAmount(amount)}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-gray-500">{tx.pending ? 'Pending' : 'Posted'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="md:hidden space-y-2 p-3">
                    {filteredTx.map((tx) => {
                      const amount = parseFloat(String(tx.amount));
                      const acctLabel = tx.accountCustomName ?? tx.accountName;
                      return (
                        <div key={tx.id} className="eadin-card p-4">
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold text-gray-500">{formatDate(tx.date)}</div>
                              <div className="break-words text-sm font-medium">{tx.name}</div>
                            </div>
                            <div className={`shrink-0 whitespace-nowrap text-right text-sm font-semibold tabular-nums ${amount > 0 ? 'value-negative' : 'value-positive'}`}>
                              {amount > 0 ? '-' : '+'}
                              {formatAmount(amount)}
                            </div>
                          </div>
                          <div className="space-y-1 text-xs text-gray-500">
                            <div className="flex items-center justify-between">
                              <span>Entity:</span>
                              <span className="border border-gray-300 px-1.5 py-0.5 text-xs">{tx.entity === 'kings_loot' ? 'KL' : 'KH'}</span>
                            </div>
                            {tx.merchantName && (
                              <div className="flex items-center justify-between">
                                <span>Merchant:</span>
                                <span className="break-words text-right">{tx.merchantName}</span>
                              </div>
                            )}
                            {acctLabel && (
                              <div className="flex items-center justify-between">
                                <span>Account:</span>
                                <span className="break-words text-right tabular-nums">
                                  {acctLabel}
                                  {tx.accountMask && <span className="ml-1 opacity-50">·{tx.accountMask}</span>}
                                </span>
                              </div>
                            )}
                            {tx.category && (
                              <div className="flex items-center justify-between">
                                <span>Category:</span>
                                <button
                                  type="button"
                                  onClick={() => setCategoryFilter(categoryFilter === tx.category ? '' : (tx.category ?? ''))}
                                  className="text-right hover:underline"
                                >
                                  {tx.category}
                                </button>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <span>Status:</span>
                              <span>{tx.pending ? 'Pending' : 'Posted'}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
